"use client";

import { useEffect } from "react";
import {
  trackEvent,
  trackSelectItem,
  trackViewItemList,
} from "@/utils/analytics";
import { recordGalleryEngagement } from "@/utils/galleryEngagement";
import type { DressType, GallerySort } from "@/types/gallery";

interface GalleryAnalyticsItem {
  id: string;
  name: string;
  category: string;
  index: number;
}

interface GalleryAnalyticsProps {
  items: GalleryAnalyticsItem[];
  itemListId: string;
  itemListName: string;
  activeCategory: DressType;
  sort: GallerySort;
  currentPage: number;
}

export default function GalleryAnalytics({
  items,
  itemListId,
  itemListName,
  activeCategory,
  sort,
  currentPage,
}: GalleryAnalyticsProps) {
  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    trackViewItemList({
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        index: item.index,
      })),
    });

    trackEvent("gallery_list_view", {
      item_list_id: itemListId,
      item_list_name: itemListName,
      gallery_category: activeCategory,
      gallery_sort: sort,
      item_count: items.length,
      page_number: currentPage,
    });
  }, [activeCategory, currentPage, itemListId, itemListName, items, sort]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const element = target.closest<HTMLElement>("[data-gallery-ticket-id]");
      if (!element) {
        return;
      }

      const itemId = element.dataset.galleryTicketId;
      const itemName = element.dataset.galleryItemName || "Custom Dress";
      const itemCategory =
        element.dataset.galleryItemCategory || activeCategory;
      const source = element.dataset.gallerySource || "card";
      const index = Number.parseInt(
        element.dataset.galleryItemIndex || "0",
        10
      );

      if (!itemId) {
        return;
      }

      trackSelectItem({
        itemId,
        itemName,
        itemCategory,
        listName: itemListName,
        index,
      });

      trackEvent("gallery_ticket_select", {
        ticket_id: itemId,
        item_id: itemId,
        item_name: itemName,
        item_category: itemCategory,
        item_list_id: itemListId,
        item_list_name: itemListName,
        gallery_category: activeCategory,
        gallery_sort: sort,
        gallery_source: source,
        index,
      });

      recordGalleryEngagement({
        ticketId: itemId,
        eventName: "gallery_ticket_select",
      });
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [activeCategory, itemListId, itemListName, sort]);

  return null;
}
